// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { Checkbox, FormControlLabel, FormGroup, FormControlLabelProps } from '@mui/material';

// ----------------------------------------------------------------------

interface RHFCheckboxProps extends Omit<FormControlLabelProps, 'control'> {
  name: string;
  isDisabled?: boolean;
  onChange?: (event: React.SyntheticEvent<Element, Event>, checked: boolean) => void;
}

export function RHFCheckbox({ name, isDisabled, onChange, ...other }: RHFCheckboxProps) {
  const { control } = useFormContext();

  return (
    <FormControlLabel
      control={
        <Controller
          name={name}
          control={control}
          render={({ field: { value, onChange: onFieldChange, ...rest } }) => (
            <Checkbox
              {...rest}
              checked={value}
              disabled={isDisabled}
              sx={{
                color: isDisabled ? 'text.disabled' : 'primary.main',
              }}
              onChange={(e) => {
                onFieldChange(e.target.checked);
                onChange && onChange(e, e.target.checked);
              }}
            />
          )}
        />
      }
      {...other}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFMultiCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'label'> {
  name: string;
  options: {
    label: string;
    value: any;
  }[];
}

export function RHFMultiCheckbox({ name, options, ...other }: RHFMultiCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const onSelected = (option: string) =>
          field.value.includes(option)
            ? field.value.filter((value: string) => value !== option)
            : [...field.value, option];

        return (
          <FormGroup>
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={field.value.includes(option.value)}
                    onChange={() => field.onChange(onSelected(option.value))}
                  />
                }
                label={option.label}
                labelPlacement={other.labelPlacement}
                {...other}
              />
            ))}
          </FormGroup>
        );
      }}
    />
  );
}
